import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, Truck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["pending", "processing", "shipped", "delivered", "cancelled"];

const inputCls = "w-full bg-[#F8F4EC] border border-[rgba(58,42,37,0.15)] rounded-lg px-4 py-2.5 text-sm text-[#2B1F1B] placeholder:text-[#9A8D85] focus:outline-none focus:border-[#3A2A25] transition-colors duration-300";

const Field = ({ label, children }) => (
  <div>
    <label className="block font-mono-accent text-[10px] uppercase tracking-[0.15em] text-[#9A8D85] mb-1.5">{label}</label>
    {children}
  </div>
);

const FulfillmentFields = ({ form, setForm }) => (
  <>
    <Field label="Fulfillment status">
      <select
        value={form.fulfillment_status}
        onChange={(e) => setForm({ ...form, fulfillment_status: e.target.value })}
        className={inputCls}
        data-testid="fulfillment-status-select"
      >
        {STATUSES.map((s) => (
          <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
        ))}
      </select>
    </Field>
    <Field label="Shipping carrier">
      <input value={form.carrier} onChange={(e) => setForm({ ...form, carrier: e.target.value })} placeholder="e.g. USPS, UPS, FedEx" className={inputCls} data-testid="carrier-input" />
    </Field>
    <Field label="Tracking number">
      <input value={form.tracking_number} onChange={(e) => setForm({ ...form, tracking_number: e.target.value })} placeholder="Tracking number" className={inputCls} data-testid="tracking-number-input" />
    </Field>
    <Field label="Tracking URL (optional)">
      <input value={form.tracking_url} onChange={(e) => setForm({ ...form, tracking_url: e.target.value })} placeholder="https://…" className={inputCls} data-testid="tracking-url-input" />
    </Field>
    <Field label="Fulfillment notes">
      <textarea value={form.fulfillment_notes} onChange={(e) => setForm({ ...form, fulfillment_notes: e.target.value })} rows={3} placeholder="Internal notes…" className={inputCls} data-testid="fulfillment-notes-input" />
    </Field>
  </>
);

const ShippingEmailNote = ({ order, form }) => (
  <>
    {order.shipping_email_sent && (
      <p className="text-xs text-[#2E7D32] flex items-center gap-2" data-testid="shipping-email-sent-note">
        <Truck size={14} /> Shipping confirmation email already sent{order.shipping_email_sent_at ? ` (${new Date(order.shipping_email_sent_at).toLocaleString()})` : ""} — it will not be sent again.
      </p>
    )}
    {!order.shipping_email_sent && form.fulfillment_status === "shipped" && (
      <p className="text-xs text-[#C9A45C] flex items-center gap-2" data-testid="shipping-email-will-send-note">
        <Truck size={14} /> Saving as Shipped will email the customer their tracking details.
      </p>
    )}
  </>
);

export const OrderManageDialog = ({ order, onClose, onSaved }) => {
  const [form, setForm] = useState({
    fulfillment_status: order.fulfillment_status || "pending",
    carrier: order.carrier || "",
    tracking_number: order.tracking_number || "",
    tracking_url: order.tracking_url || "",
    fulfillment_notes: order.fulfillment_notes || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/admin/orders/${order.id}`, form, {
        withCredentials: true,
      });
      toast.success(
        data.fulfillment_status === "shipped" && data.shipping_email_sent
          ? "Order updated — shipping confirmation email sent"
          : "Order updated"
      );
      onSaved(data);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not update order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-[#241813]/40 backdrop-blur-sm" onClick={onClose} data-testid="manage-dialog-overlay" />
      <div className="relative w-full max-w-md bg-[#FFFDF9] rounded-2xl border border-[rgba(58,42,37,0.1)] shadow-[0_30px_60px_rgba(58,42,37,0.2)] p-8 max-h-[90vh] overflow-y-auto" data-testid="manage-order-dialog">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-serif-display text-2xl text-[#2B1F1B]">Manage Order</h3>
            <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#C9A45C] mt-1">{order.order_number || order.session_id?.slice(0, 18)}</p>
          </div>
          <button onClick={onClose} className="p-2 text-[#756A64] hover:text-[#3A2A25] transition-colors duration-300" data-testid="manage-dialog-close">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4">
          <FulfillmentFields form={form} setForm={setForm} />
          <ShippingEmailNote order={order} form={form} />

          <button
            onClick={save}
            disabled={saving}
            className="w-full px-8 py-3.5 bg-[#3A2A25] text-white text-xs font-semibold uppercase tracking-[0.2em] rounded-full hover:bg-[#55423A] transition-colors duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="save-order-button"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};
